import { pdpSelectors } from '@ynap/product-details-state'
import { constructUrl } from '@ynap/url-configuration'
import { productSelectors } from '@ynap/product-state'
import getCart from './getCart'

const {
    getProductColours,
    getSelectedProductColour,
    getSelectedSize,
    getSelectedProductImages,
    getSelectedProductPrice,
    getSelectedProductBadges,
    getColourSwatches,
} = pdpSelectors

const { getBuyableStatus } = productSelectors

import { createCombinedSelector } from '../utils/selectorUtils'
import { Config, Locale, StoreState } from 'app/state/types'
import { Messages, Product, ShoppingCart } from 'app/types/common-types'

export default createCombinedSelector({
    name: 'getProductDetails',
    state: ['locale', 'config', 'cart', 'deviceType', 'cartProductDetails'],
    props: ['messages'],
    fn: getProductDetailsInternal,
})

interface State extends StoreState {
    cart: ShoppingCart
    config: Config
    cartProductDetails: {
        error: null
        loading: boolean
        partNumber: string
        product: Product
    }
    locale: Locale
    deviceType: string
}

interface Props {
    messages: Messages
}

export function getProductDetailsInternal(state: State, ownProps: Props) {
    const { cart, config, cartProductDetails, locale } = state
    const { orderItem: orderItems = [] } = cart // Rename as it's an array
    const { loading, error, product, partNumber } = cartProductDetails
    const { messages } = ownProps
    const orderItem = orderItems.find((orderItem) => orderItem.partNumber === partNumber)

    const defaultProps = {
        product: {},
        productUrl: '',
        messages,
        locale: locale.id,
        quantity: {
            initialQuantity: 1,
            minQuantity: 1,
            maxQuantity: 1,
        },
        wishlistId: null,
        loading: true,
        errors: {},
        cartLineItem: {},
    }

    if (loading || error) return defaultProps // TODO: Handle errors appropriately
    // Strictly speaking `selectedSku` won't be `cartProductDetails.partNumber` if a different `productColour` has been selected
    // using the swatch selector. However getSelectedSize will still correctly return an empty object as `cartProductDetails.partNumber`
    // will not be present in `selectedProductColour.sKUs
    // This distinction is currently only important in the context of the test where we mock `getSelectedSize`, however there is a
    // TODO comment in cartProductDetails.js which would indirectly address this issue
    const singleSkuItem = product?.productColours?.[0]?.sKUs?.length === 1 && { partNumber: product?.productColours?.[0]?.sKUs?.[0]?.partNumber }
    const selectedSku = product.selectedSku || singleSkuItem || (!product.isRecommendation && { partNumber })
    const productColours = getProductColours(product)
    const selectedProductColour = getSelectedProductColour(productColours)
    const selectedProductSize = getSelectedSize(selectedProductColour.sKUs, selectedSku)
    const selectedProduct = { ...selectedProductColour, ...selectedProductSize }
    const availableImageSizes = config.head ? config.head.availableImageSizes : config.availableImageSizes // config.availableImageSizes for legacy config
    const imagesphereAspectRatio = config?.head?.aspectRatio?.imagesphere
    const selectedProductVariant = {
        ...selectedProduct,
        images: getSelectedProductImages(product, selectedProductColour, availableImageSizes, undefined, null, imagesphereAspectRatio),
        price: getSelectedProductPrice(selectedProduct),
        badges: getSelectedProductBadges(selectedProductColour, selectedProductSize),
        colourPartNumber: selectedProductColour.partNumber,
        colourSwatches: getColourSwatches(productColours),
        buyable: getBuyableStatus({ selectedProduct, locale: locale.id, messages }),
    }

    // TODO: see if some logic can be shared with @ynap/product-state/src/selectors/getProductLinkByPartNumber.js
    const productUrlTemplate = config?.global?.services?.urlConfig?.pdp?.construct || ''
    const seoURLKeyword = selectedProduct && selectedProduct.seo ? selectedProduct.seo.seoURLKeyword : ''

    const productUrl = constructUrl(productUrlTemplate, {
        LOCALE: locale.id,
        SEO_URLKEYWORD: seoURLKeyword,
    })

    const maxQuantity =
        orderItem?.partNumber === selectedProduct.partNumber
            ? (orderItem.InventoryAvailability.length > 0 && orderItem.InventoryAvailability[0].onlineInventory) || 0
            : selectedProduct.oneLeftOnline
            ? 1
            : selectedProduct.soldOutOnline
            ? 0
            : 10

    const cartLineItem = getCart(state, ownProps)?.lineItems?.find((item) => item.partNumber === partNumber)

    if (selectedProductVariant.finalSale) {
        selectedProductVariant.buyable.notification = { message: ownProps.messages.notifications.finalSale() }
    }

    product.designerName = product.designerNameEN || product.designerName

    const consideredBadge = product.attributes?.find((attribute) => {
        return config?.head?.sustainabilityAttributes?.includes?.(attribute.identifier) || attribute?.identifier === 'CONSIDERED'
    })

    return {
        ...defaultProps,
        designerCartItemLimit: Number(orderItem?.catalogEntryView?.designerCartItemLimit),
        product,
        consideredBadge: consideredBadge?.label ? consideredBadge?.label : null,
        productUrl,
        selectedProductVariant,
        selectedSku: selectedSku.partNumber ? selectedSku : { partNumber: selectedProductSize.partNumber },
        selectedSizeLabel: selectedProductSize.size && selectedProductSize.size.labelSize,
        errors: {
            addToCart: error,
        },
        quantity: !product.isRecommendation
            ? {
                  initialQuantity: orderItem ? orderItem.quantity : 1,
                  minQuantity: Math.min(1, maxQuantity),
                  maxQuantity,
              }
            : null,
        wishlistId: null,
        loading: false,
        cartLineItem,
        partOfBasket: !!orderItem,
    }
}
